import type { BffLogicalDeviceResourceReadApi } from '@ingenico/api-sdk-provisioning-private';
import type { BusinessServiceRequest } from '@ingenico/react-section-utils';
import { useBusinessServiceRequest } from '@ingenico/react-section-utils';
import { cloneDeep, isArray, isEmpty, isEqual } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DeviceManagementService } from '../domain/business/services/deviceManagement.service';
import type { DeviceBaseInfo, DeviceMetrics } from '../domain/models';
import type {
  DeviceCallScheduling,
  EnumScreenLockingStatus,
  NonRequiredDeviceConnectionStatus,
} from '../domain/models/deviceInfo.model';
import { useDevicePropertiesRequestTimer } from './useDevicePropertiesRequestTimer.hook';
import { useGetDeviceIdV2 } from './useGetDeviceIdV2.hook';
import { useLogicalDeviceConfigurations } from './useLogicalDeviceConfigurations.hook';
import { useProcessRequest } from './useProcessDeviceRequest';

export interface LogicalDeviceConfiguration {
  readonly connectionStatus?: NonRequiredDeviceConnectionStatus | null;
  readonly deviceMetrics?: DeviceBaseInfo | DeviceMetrics | null | undefined;
  readonly callScheduling?: DeviceCallScheduling;
  readonly screenLockingStatus?: EnumScreenLockingStatus;
  readonly isRtsEnabled?: boolean;
  readonly isDeviceMetricsError?: boolean;
  readonly isDeviceMetricsSuccessfull?: boolean;
  readonly isFirstLoadingMetrics?: boolean;
  readonly isLoadingDeviceConfiguration?: boolean;
  readonly isRefetchingDeviceConfiguration?: boolean;
}

export interface UseLogicalDevicesDetailsReturn {
  readonly handleRefreshDeviceProperties: () => void;
  readonly logicalDeviceConfiguration: LogicalDeviceConfiguration;
  readonly logicalDeviceResponse: BusinessServiceRequest<BffLogicalDeviceResourceReadApi>;
}

export interface UseLogicalDevicesDetailsProps {
  readonly logicalDeviceId: string;
  readonly onAskRefetchSuccess?: () => void;
  readonly shouldFetch?: boolean;
}

export function useLogicalDevicesDetails({
  logicalDeviceId,
  onAskRefetchSuccess,
  shouldFetch = false,
}: UseLogicalDevicesDetailsProps): UseLogicalDevicesDetailsReturn {
  const [logicalDeviceDataToTriggerRequest, setLogicalDeviceDataToTriggerRequest] = useState<{
    readonly id: string;
  }>({ id: '' });
  const [isRefetchActionOngoing, setIsRefetchActionOngoing] = useState<boolean>(false);

  const keepImperativeRefetchDeviceFlow = useRef<boolean>(false);

  const logicalDeviceResponse = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (deviceManagementService, isRefetch) =>
        deviceManagementService.getLogicalDeviceDetailsBff(logicalDeviceId, { isRefetch }),
      [logicalDeviceId],
    ),
  );

  const { metricsProcessed } = useLogicalDeviceConfigurations(
    useMemo(
      () => ({
        logicalDeviceInfo: logicalDeviceDataToTriggerRequest,
        shouldFetch,
      }),
      [logicalDeviceDataToTriggerRequest, shouldFetch],
    ),
  );

  const { isFirstLoading: isFirstLoadingDeviceIdV2, ...getDeviceIdRequest } = useGetDeviceIdV2({
    deviceId: logicalDeviceId,
    shouldFetch,
  });

  const processedGetDeviceIdV2Request = useProcessRequest({
    requestQuery: getDeviceIdRequest,
    isFirstLoading: isFirstLoadingDeviceIdV2,
  });

  const {
    hasAllPrevConfigurationRequestSuccedded,
    hasAnyPrevConfigurationRequestSuccedded,
    hasAllPrevConfigurationRequestFailed,
  } = useMemo(
    () => ({
      hasAllPrevConfigurationRequestSuccedded:
        processedGetDeviceIdV2Request.hasAllPreviousRequestSuccedded &&
        metricsProcessed?.hasAllPreviousRequestSuccedded,
      hasAnyPrevConfigurationRequestSuccedded:
        processedGetDeviceIdV2Request.hasAllPreviousRequestSuccedded &&
        metricsProcessed?.hasAllPreviousRequestSuccedded,
      hasAllPrevConfigurationRequestFailed:
        [processedGetDeviceIdV2Request.isWasFetching, metricsProcessed?.isWasFetching].every((el) => el === true) &&
        [processedGetDeviceIdV2Request.isError, metricsProcessed?.isError].some((el) => isEqual(true, el)),
    }),
    [
      metricsProcessed?.hasAllPreviousRequestSuccedded,
      metricsProcessed?.isError,
      metricsProcessed?.isWasFetching,
      processedGetDeviceIdV2Request.hasAllPreviousRequestSuccedded,
      processedGetDeviceIdV2Request.isError,
      processedGetDeviceIdV2Request.isWasFetching,
    ],
  );
  const { metricsData, isFirstLoadingMetrics, isSuccess } = useMemo(() => {
    // If shouldFetch is false or logicalDeviceId is empty, don't show loading
    const shouldShowMetricsLoading = shouldFetch && Boolean(logicalDeviceId);

    return {
      metricsData: metricsProcessed?.data,
      isMetrictsError: metricsProcessed?.isError,
      isFirstLoadingMetrics: shouldShowMetricsLoading && metricsProcessed?.isFirstLoading && !metricsProcessed?.isError,
      isSuccess: metricsProcessed?.isSuccess,
    };
  }, [
    metricsProcessed?.data,
    metricsProcessed?.isError,
    metricsProcessed?.isFirstLoading,
    metricsProcessed?.isSuccess,
    shouldFetch,
    logicalDeviceId,
  ]);

  const [latestSuccessfullMetricsData, setLatestSuccessfullMetricsData] = useState(metricsData);
  const [latestSuccessfullDeviceDetails, setLatestSuccessfullDeviceDetails] = useState(
    processedGetDeviceIdV2Request?.data,
  );

  useEffect(() => {
    if (isSuccess) {
      setLatestSuccessfullMetricsData(metricsData);
    }
  }, [isSuccess, metricsData]);
  useEffect(() => {
    if (processedGetDeviceIdV2Request?.isSuccess) {
      setLatestSuccessfullDeviceDetails(processedGetDeviceIdV2Request?.data);
    }
  }, [isSuccess, metricsData, processedGetDeviceIdV2Request?.data, processedGetDeviceIdV2Request?.isSuccess]);

  const isRtsEnabled = useMemo(
    () => isEqual(latestSuccessfullDeviceDetails?.isRtsEnabled, true),
    [latestSuccessfullDeviceDetails?.isRtsEnabled],
  );

  const callScheduling = useMemo(
    () => latestSuccessfullDeviceDetails?.callScheduling,
    [latestSuccessfullDeviceDetails?.callScheduling],
  );

  const screenLockingStatus = useMemo(
    () => latestSuccessfullDeviceDetails?.screenLockingStatus,
    [latestSuccessfullDeviceDetails?.screenLockingStatus],
  );

  const connectionStatus = useMemo(() => {
    return latestSuccessfullDeviceDetails?.connection;
  }, [latestSuccessfullDeviceDetails?.connection]);

  const deviceConnectionStatus = useMemo(() => {
    return connectionStatus?.connectionStatus;
  }, [connectionStatus?.connectionStatus]);

  const {
    handleRefreshDeviceProperties: handleRefreshOfMetricsUpdates,
    askDeviceUpdateRequestState: { isLoading: isLoadingSnapshotRequest },
  } = useDevicePropertiesRequestTimer({
    logicalDeviceId,
    onAskRefetchError: () => setIsRefetchActionOngoing(false),
    onAskRefetchSuccess: () => {
      keepImperativeRefetchDeviceFlow.current = true;
      setLogicalDeviceDataToTriggerRequest({
        id: cloneDeep(logicalDeviceId),
      });
      onAskRefetchSuccess?.();
    },
    onAskRefetchInit: () =>
      setLogicalDeviceDataToTriggerRequest({
        id: cloneDeep(logicalDeviceId),
      }),
    hasError: false,
    deviceConnectionStatus,
  });

  const {
    handleRefreshDeviceProperties: handleDetailsOfDeviceRefresh,
    askDeviceUpdateRequestState: { isLoading: isLoadingDetailsOfADevice },
  } = useDevicePropertiesRequestTimer({
    logicalDeviceId,
    customRequest: () => {
      getDeviceIdRequest.refetch();
    },
    hasError: false,
    deviceConnectionStatus,
  });

  useEffect(() => {
    if (
      keepImperativeRefetchDeviceFlow.current &&
      isRefetchActionOngoing &&
      (hasAllPrevConfigurationRequestSuccedded ||
        hasAnyPrevConfigurationRequestSuccedded ||
        hasAllPrevConfigurationRequestFailed)
    ) {
      keepImperativeRefetchDeviceFlow.current = false;
      setIsRefetchActionOngoing(false);
    }
  }, [
    isRefetchActionOngoing,
    hasAllPrevConfigurationRequestSuccedded,
    hasAnyPrevConfigurationRequestSuccedded,
    hasAllPrevConfigurationRequestFailed,
  ]);

  const isRefetchingDeviceConfiguration = useMemo(
    () =>
      metricsProcessed?.isRefetching ||
      isLoadingSnapshotRequest ||
      processedGetDeviceIdV2Request?.isRefetching ||
      isLoadingDetailsOfADevice,
    [
      metricsProcessed?.isRefetching,
      isLoadingSnapshotRequest,
      processedGetDeviceIdV2Request?.isRefetching,
      isLoadingDetailsOfADevice,
    ],
  );

  const deviceMetrics = useMemo<DeviceBaseInfo | DeviceMetrics | undefined>(() => {
    return !isArray(latestSuccessfullMetricsData) && !isEmpty(latestSuccessfullMetricsData)
      ? { ...latestSuccessfullMetricsData, ...latestSuccessfullDeviceDetails?.baseInfo }
      : latestSuccessfullDeviceDetails?.baseInfo;
  }, [latestSuccessfullMetricsData, latestSuccessfullDeviceDetails?.baseInfo]);

  const handleRefreshDeviceProperties = useCallback(() => {
    handleRefreshOfMetricsUpdates();
    handleDetailsOfDeviceRefresh();
  }, [handleDetailsOfDeviceRefresh, handleRefreshOfMetricsUpdates]);
  return {
    logicalDeviceResponse,
    logicalDeviceConfiguration: {
      callScheduling,
      connectionStatus,
      deviceMetrics,
      screenLockingStatus,
      isRtsEnabled,
      isDeviceMetricsError: metricsProcessed?.isError,
      isDeviceMetricsSuccessfull: metricsProcessed?.isSuccess,
      isFirstLoadingMetrics,
      isLoadingDeviceConfiguration: metricsProcessed?.isLoading || processedGetDeviceIdV2Request.isLoading,
      isRefetchingDeviceConfiguration,
    },
    handleRefreshDeviceProperties,
  };
}
